import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../auth/AuthProvider";
import { useCustomerId } from "../../hooks/useCustomerId";
import { listPortfolios, createPortfolio } from "../../api/loadPortfolios";
import {
  PageLayout,
  PageHeader,
  Card,
  Button,
  Badge,
  PageSpinner,
  EmptyState,
} from "../../components";
import { LoadPortfolioCreateModal } from "../../features/load-portfolio/components/LoadPortfolioCreateModal";
import type {
  LoadPortfolioSummary,
  LoadPortfolioCreateInput,
} from "../../features/load-portfolio/domain/types";
import { SOURCE_TYPE_COLORS } from "../../features/load-portfolio/domain/types";

function StatusBadge({ status }: { status: string | null }) {
  if (!status) return <Badge>No runs</Badge>;
  const colors: Record<string, string> = {
    completed: "text-green-400 bg-green-400/10",
    running: "text-quantum-400 bg-quantum-400/10",
    queued: "text-yellow-400 bg-yellow-400/10",
    failed: "text-red-400 bg-red-400/10",
  };
  return (
    <span className={`px-2 py-0.5 rounded text-body-sm ${colors[status] || ""}`}>
      {status}
    </span>
  );
}

export default function LoadPortfolios() {
  const { token } = useAuth();
  const customerId = useCustomerId(token);
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);

  const { data: portfolios, isLoading } = useQuery<LoadPortfolioSummary[]>({
    queryKey: ["dc-portfolios"],
    queryFn: () => listPortfolios(token!),
    enabled: !!token,
  });

  const handleCreate = async (data: LoadPortfolioCreateInput) => {
    await createPortfolio(data, token!);
    queryClient.invalidateQueries({ queryKey: ["dc-portfolios"] });
  };

  if (isLoading) return <PageSpinner message="Loading portfolios..." />;

  return (
    <PageLayout>
      <PageHeader
        title="Load Portfolios"
        subtitle="Behind-the-meter generation portfolio impact assessments"
        actions={
          <Button variant="primary" onClick={() => setShowCreate(true)}>
            New Portfolio
          </Button>
        }
      />

      {portfolios && portfolios.length > 0 ? (
        <div className="space-y-3">
          {portfolios.map((p) => (
            <Link
              key={p.portfolio_id}
              to={p.portfolio_id}
              className="block"
            >
              <Card>
                <div className="p-4 flex items-center justify-between hover:bg-surface-600 transition-colors rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <span className="text-body font-medium text-text-primary">
                        {p.portfolio_name}
                      </span>
                      <StatusBadge status={p.latest_run_status} />
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-body-sm text-text-secondary">
                      {p.state && <span>{p.state}</span>}
                      {p.eia_ba_code && <span>{p.eia_ba_code}</span>}
                      {p.flat_load_mw && <span>{p.flat_load_mw} MW load</span>}
                      <span>PUE {p.pue}</span>
                      <span>{p.source_count} source{p.source_count !== 1 ? "s" : ""}</span>
                    </div>
                  </div>
                  <svg
                    className="w-5 h-5 text-text-muted"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      ) : (
        <EmptyState
          title="No load portfolios"
          action={{
            label: "Create Portfolio",
            onClick: () => setShowCreate(true),
          }}
        />
      )}

      <LoadPortfolioCreateModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        customerId={customerId || ""}
        onSubmit={handleCreate}
      />
    </PageLayout>
  );
}
